// Web port of mobile/src/components/ReportDoorPicker.tsx — "Doors in this
// report" picker for the Building Report. Lets the author include every door
// or a selection (e.g. only doors re-inspected after repairs). value === null
// means "all doors" (default) so existing behaviour is unchanged.
import React, { useMemo, useState } from "react";
import { CheckSquare, Square, Search, ChevronDown, ChevronUp } from "lucide-react";
import { effectiveStatus } from "@/pdf/reportChecklist";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";

export const latestInspectionByDoor = (inspections) => {
  const m = new Map();
  for (const insp of inspections || []) {
    const id = String(insp?.door_id || "");
    if (!id) continue;
    const prev = m.get(id);
    const ts = new Date(insp.inspection_date || 0).getTime();
    if (!prev || ts > new Date(prev.inspection_date || 0).getTime()) m.set(id, insp);
  }
  return m;
};

export const buildDoorPicks = (doors, inspections) => {
  const latest = latestInspectionByDoor(inspections);
  return (doors || []).map((d) => {
    const insp = latest.get(String(d.id));
    return {
      id: String(d.id),
      door_reference: d.door_reference || d.location || "Door",
      location: d.location || null,
      status: insp ? String(effectiveStatus(insp)) : "not_inspected",
      lastAt: insp ? new Date(insp.inspection_date || 0).getTime() : null,
    };
  });
};

const STATUS_LABEL = {
  pass: { label: "Satisfactory", color: "text-emerald-700", bg: "bg-emerald-100" },
  pass_with_observations: { label: "Satisfactory (adv.)", color: "text-sky-700", bg: "bg-sky-100" },
  fail: { label: "Remedial", color: "text-red-700", bg: "bg-red-100" },
  review: { label: "Further review", color: "text-amber-700", bg: "bg-amber-100" },
  no_access: { label: "No access", color: "text-slate-700", bg: "bg-slate-200" },
  not_inspected: { label: "Not inspected", color: "text-slate-500", bg: "bg-slate-100" },
};

const DAY = 24 * 60 * 60 * 1000;

const quickPick = (key, picks) => {
  const now = Date.now();
  switch (key) {
    case "all": return null;
    case "passed": return picks.filter((p) => p.status === "pass" || p.status === "pass_with_observations").map((p) => p.id);
    case "failed": return picks.filter((p) => p.status === "fail" || p.status === "review").map((p) => p.id);
    case "recent7": return picks.filter((p) => p.lastAt && now - p.lastAt <= 7 * DAY).map((p) => p.id);
    case "recent30": return picks.filter((p) => p.lastAt && now - p.lastAt <= 30 * DAY).map((p) => p.id);
    case "not_inspected": return picks.filter((p) => p.status === "not_inspected").map((p) => p.id);
    default: return null;
  }
};

export const selectionLabel = (picks, value) => {
  if (value === null) return `All ${picks.length} door${picks.length === 1 ? "" : "s"}`;
  return `${value.length} of ${picks.length} doors selected`;
};

export default function ReportDoorPicker({ picks, value, onChange, testId = "report-doors" }) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const selected = useMemo(() => new Set(value === null ? picks.map((p) => p.id) : value), [value, picks]);
  const counts = useMemo(() => ({
    passed: picks.filter((p) => p.status === "pass" || p.status === "pass_with_observations").length,
    failed: picks.filter((p) => p.status === "fail" || p.status === "review").length,
    recent7: (quickPick("recent7", picks) || []).length,
    recent30: (quickPick("recent30", picks) || []).length,
    not_inspected: picks.filter((p) => p.status === "not_inspected").length,
  }), [picks]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next.size === picks.length ? null : Array.from(next));
  };

  const filtered = picks.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return p.door_reference.toLowerCase().includes(q) || String(p.location || "").toLowerCase().includes(q);
  });

  const quick = [
    { key: "all", label: "All doors", count: picks.length },
    { key: "passed", label: "Passed", count: counts.passed },
    { key: "failed", label: "Failed / review", count: counts.failed },
    { key: "recent7", label: "Inspected last 7 days", count: counts.recent7 },
    { key: "recent30", label: "Inspected last 30 days", count: counts.recent30 },
    { key: "not_inspected", label: "Not inspected", count: counts.not_inspected },
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-primary">
          <CheckSquare className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">Doors in this report</p>
          <p className="mt-0.5 text-xs text-slate-500">Include every door, or just a selection — e.g. only the doors re-inspected after repairs.</p>
          <p className="mt-1.5 text-sm font-semibold text-primary" data-testid={`${testId}-summary`}>{selectionLabel(picks, value)}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {quick.map((q) => {
          const ids = quickPick(q.key, picks);
          const active = q.key === "all"
            ? value === null
            : value !== null && ids !== null && ids.length === value.length && ids.every((id) => selected.has(id)) && ids.length > 0;
          const disabled = q.key !== "all" && q.count === 0;
          return (
            <button
              key={q.key}
              type="button"
              disabled={disabled}
              onClick={() => { if (!disabled) onChange(q.key === "all" ? null : ids); }}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                active ? "border-primary bg-red-50 text-red-700" : "border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200"
              } ${disabled ? "cursor-not-allowed opacity-45" : ""}`}
              data-testid={`${testId}-quick-${q.key}`}
            >
              {q.label} ({q.count})
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="mt-3 flex w-full items-center justify-between text-sm font-semibold text-primary"
        data-testid={`${testId}-expand`}
      >
        {expanded ? "Hide door list" : "Choose doors individually"}
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {expanded && (
        <div className="mt-3">
          {picks.length > 8 && (
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search door reference or location"
                className="pl-9"
                data-testid={`${testId}-search`}
              />
            </div>
          )}
          <div className="mb-2 flex gap-4 text-xs font-semibold text-slate-600">
            <button type="button" className="underline" onClick={() => onChange(null)} data-testid={`${testId}-select-all`}>Select all</button>
            <button type="button" className="underline" onClick={() => onChange([])} data-testid={`${testId}-clear`}>Clear</button>
          </div>
          <div className="max-h-80 space-y-0 overflow-auto">
            {filtered.length === 0 && <p className="py-3 text-center text-xs text-slate-500">No doors match.</p>}
            {filtered.map((p) => {
              const on = selected.has(p.id);
              const st = STATUS_LABEL[p.status] || STATUS_LABEL.not_inspected;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(p.id)}
                  className="flex w-full items-center gap-3 border-t border-slate-100 py-2.5 text-left"
                  data-testid={`${testId}-door-${p.id}`}
                >
                  {on ? <CheckSquare className="h-5 w-5 shrink-0 text-primary" /> : <Square className="h-5 w-5 shrink-0 text-slate-400" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{p.door_reference}</p>
                    <p className="truncate text-xs text-slate-500">
                      {[p.location, p.lastAt ? `Inspected ${fmtDate(new Date(p.lastAt))}` : "Not inspected"].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${st.bg} ${st.color}`}>{st.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
